import { callApi, extractApiErrorMessage } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import { normalizeString } from "@src/utils/string-utils";
import ClientLogsService from "./client-logs.service";
import { buildPollingStatusConfig } from "./configs";
import { runManagedRequest } from "./request-manager";

interface BackendPollingStatusResponse {
  polling_id?: string;
  message?: string;
  transactionid?: string;
  transactionId?: string;
  steps?: BackendPollingStatusStep[];
}

interface BackendPollingStatusStep {
  step_name?: string;
  status?: string;
  error_msg?: string | null;
  started_ts?: string | null;
  completed_ts?: string | null;
}

export type PollingStepName =
  | "Create Patient"
  | "Create Insurance"
  | "Create Appointment"
  | "Send Portal Invite"
  | "Eligibility Check";

export type PollingStepStatus = "pending" | "in_progress" | "complete" | "failed" | "skipped";

export interface PollingStatusStep {
  stepName: PollingStepName;
  status: PollingStepStatus;
  errorMessage?: string | null;
  startedTimestamp?: string | null;
  completedTimestamp?: string | null;
}

export interface PollingStatusResponse {
  // Indicates whether the polling-status API request itself succeeded.
  success: boolean;
  message: string;
  pollingId?: string;
  status?: string;
  transactionId?: string;
  steps: PollingStatusStep[];
  criticalStepsComplete: boolean;
  criticalStepsFailed: boolean;
  // Indicates whether critical polling steps reached a terminal state (all complete or any failed).
  isTerminal: boolean;
  // Indicates a successful terminal business outcome (critical steps complete and not failed).
  isSuccess: boolean;
}

const SUPPORTED_STEP_NAMES: readonly PollingStepName[] = [
  "Create Patient",
  "Create Insurance",
  "Create Appointment",
  "Send Portal Invite",
  "Eligibility Check",
];
const CRITICAL_STEP_NAMES: readonly PollingStepName[] = [
  "Create Patient",
  "Create Insurance",
  "Create Appointment",
];
const SUPPORTED_STEP_STATUSES = new Set<PollingStepStatus>([
  "pending",
  "in_progress",
  "complete",
  "failed",
  "skipped",
]);
const STEP_NAME_LOOKUP = new Map(
  SUPPORTED_STEP_NAMES.map((name) => [name.toLowerCase(), name] as const),
);

const normalizeStatus = (status: unknown): string => {
  return normalizeString(status)?.toLowerCase() ?? "";
};

const normalizeStepName = (stepName: unknown): PollingStepName | null => {
  const normalized = normalizeString(stepName)?.toLowerCase();
  if (!normalized) {
    return null;
  }

  return STEP_NAME_LOOKUP.get(normalized) ?? null;
};

const normalizeStepStatus = (status: unknown): PollingStepStatus | null => {
  const normalized = normalizeStatus(status);
  if (!SUPPORTED_STEP_STATUSES.has(normalized as PollingStepStatus)) {
    return null;
  }

  return normalized as PollingStepStatus;
};

const parseSteps = (steps: BackendPollingStatusStep[] | undefined): PollingStatusStep[] => {
  if (!Array.isArray(steps)) {
    return [];
  }

  return steps.reduce<PollingStatusStep[]>((acc, step) => {
    const stepName = normalizeStepName(step.step_name);
    const stepStatus = normalizeStepStatus(step.status);
    if (!stepName || !stepStatus) {
      return acc;
    }

    acc.push({
      stepName,
      status: stepStatus,
      errorMessage: step.error_msg,
      startedTimestamp: step.started_ts,
      completedTimestamp: step.completed_ts,
    });
    return acc;
  }, []);
};

const getCriticalStepOutcome = (steps: PollingStatusStep[]) => {
  const latestStepStatusByName = new Map<PollingStepName, PollingStepStatus>();
  for (const step of steps) {
    latestStepStatusByName.set(step.stepName, step.status);
  }

  const criticalStepsFailed = CRITICAL_STEP_NAMES.some((stepName) => {
    return latestStepStatusByName.get(stepName) === "failed";
  });

  const criticalStepsComplete = CRITICAL_STEP_NAMES.every((stepName) => {
    const status = latestStepStatusByName.get(stepName);
    return status === "complete" || status === "skipped";
  });

  return {
    criticalStepsComplete,
    criticalStepsFailed,
  };
};

export class PollingStatusService {
  static async getPollingStatus(
    pollingId: string,
    userUuid?: string,
  ): Promise<PollingStatusResponse> {
    const trimmedPollingId = pollingId.trim();
    const key = `polling-status:${userUuid ?? "unknown"}:${trimmedPollingId || "missing"}`;

    if (!trimmedPollingId) {
      return {
        success: false,
        message: "Missing pollingId",
        steps: [],
        criticalStepsComplete: false,
        criticalStepsFailed: false,
        isTerminal: true,
        isSuccess: false,
      };
    }

    try {
      const { data, response } = await runManagedRequest({
        key,
        policy: "dedupe-in-flight",
        request: async () => {
          return callApi(buildPollingStatusConfig(trimmedPollingId, userUuid));
        },
      });
      const payload = (data ?? {}) as BackendPollingStatusResponse;
      const steps = parseSteps(payload.steps);
      const { criticalStepsComplete, criticalStepsFailed } = getCriticalStepOutcome(steps);
      const isTerminal = criticalStepsComplete || criticalStepsFailed;
      const isSuccess = criticalStepsComplete && !criticalStepsFailed;

      if (!response.ok) {
        const responseError = extractApiErrorMessage(data, response);

        ClientLogsService.logError(
          userUuid || "unknown",
          "polling-status.service.getPollingStatus",
          "Polling status request failed",
          new Error(responseError),
          {
            errorType: getFailureTypeForHttpStatus(response.status),
            dependencyName: "PollingStatusAPI",
            functionName: "getPollingStatus",
          },
        );

        const responseToReturn = {
          success: false,
          message: responseError,
          pollingId: normalizeString(payload.polling_id) || trimmedPollingId,
          transactionId: payload.transactionId || payload.transactionid,
          steps,
          criticalStepsComplete,
          criticalStepsFailed,
          isTerminal: true,
          isSuccess: false,
        };
        return responseToReturn;
      }

      const responseToReturn = {
        success: true,
        message: (() => {
          if (!isTerminal) {
            return normalizeString(payload.message) || "Polling in progress";
          }

          return isSuccess ? "Critical steps completed successfully" : "Critical steps failed";
        })(),
        pollingId: normalizeString(payload.polling_id) || trimmedPollingId,
        transactionId: payload.transactionId || payload.transactionid,
        steps,
        criticalStepsComplete,
        criticalStepsFailed,
        isTerminal,
        isSuccess,
      };

      return responseToReturn;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      ClientLogsService.logError(
        userUuid || "unknown",
        "polling-status.service.getPollingStatus",
        "Error getting polling status",
        error instanceof Error ? error : new Error(errorMessage),
        {
          errorType: "DependencyFailure",
          dependencyName: "PollingStatusAPI",
          functionName: "getPollingStatus",
        },
      );

      return {
        success: false,
        message: errorMessage,
        steps: [],
        criticalStepsComplete: false,
        criticalStepsFailed: false,
        isTerminal: true,
        isSuccess: false,
      };
    }
  }
}

export default PollingStatusService;
